const MAX_RECENT_AREAS = 4;
const STORAGE_PREFIX = "partskick.recentAreas";

export const AREA_CATALOG = {
  "/workstation-manager": {
    icon: "bi-pc-display",
    title: "Workstations",
    description: "Manage workstation registrations and status.",
  },
  "/workstation-manager-view": {
    icon: "bi-pc-display-horizontal",
    title: "Workstation View",
    description: "Review workstation details.",
  },
  "/mobile-device-manager": {
    icon: "bi-phone",
    title: "Mobile Devices",
    description: "Review mobile device access.",
  },
  "/registerNewClient": {
    icon: "bi-building-add",
    title: "Register Client",
    description: "Create a new client account.",
  },
  "/createUser": {
    icon: "bi-person-plus",
    title: "Create Owner",
    description: "Create a new owner user.",
  },
  "/resetUserPassword": {
    icon: "bi-key",
    title: "Reset User Password",
    description: "Reset a portal user's password.",
  },
  "/clientDiscount": {
    icon: "bi-percent",
    title: "Client Discount",
    description: "Set client discount rules.",
  },
  "/uploadMessagePage": {
    icon: "bi-chat-square-text",
    title: "Message Page",
    description: "Update portal messages.",
  },
  "/admin/install-keys": {
    icon: "bi-key-fill",
    title: "Install Keys",
    description: "Manage install keys.",
  },
  "/admin/pk-clients": {
    icon: "bi-buildings",
    title: "PK Clients",
    description: "Manage PartsKick clients.",
  },
  "/admin/pk-groups": {
    icon: "bi-collection",
    title: "PK Groups",
    description: "Manage PartsKick groups.",
  },
  "/media-client": {
    icon: "bi-camera",
    title: "Media Clients",
    description: "Manage PhotoBase media clients.",
  },
  "/admin/gpt-configure": {
    icon: "bi-robot",
    title: "GPT Configure",
    description: "Manage AI configuration.",
  },
  "/subscriptions-moneris": {
    icon: "bi-credit-card",
    title: "Moneris",
    description: "Manage Moneris subscriptions.",
  },
  "/subscriptions-stripe": {
    icon: "bi-credit-card-2-front",
    title: "Stripe",
    description: "Manage Stripe subscriptions.",
  },
  "/inventory": {
    icon: "bi-box-seam",
    title: "Inventory",
    description: "Open the inventory list.",
  },
  "/inventoryValue": {
    icon: "bi-graph-up-arrow",
    title: "Inventory Value",
    description: "Review inventory value.",
  },
  "/soldHistory": {
    icon: "bi-clock-history",
    title: "Sold History",
    description: "Review sold inventory history.",
  },
  "/inventory/parts-ai-result": {
    icon: "bi-stars",
    title: "Parts AI Result",
    description: "Review AI part number results.",
  },
  "/reports/client-shop-name": {
    icon: "bi-shop",
    title: "Shop Names",
    description: "Review workstation shop names.",
  },
  "/reports/shop-white-list": {
    icon: "bi-shield-check",
    title: "Shop White List",
    description: "Manage approved shops.",
  },
  "/reports/shop-black-list": {
    icon: "bi-shield-x",
    title: "Shop Black List",
    description: "Manage blocked shops.",
  },
  "/reports/blocked-workstations": {
    icon: "bi-slash-circle",
    title: "Blocked Workstations",
    description: "Review blocked workstations.",
  },
  "/reports/trespass-requests": {
    icon: "bi-envelope-exclamation",
    title: "Trespass Requests",
    description: "Review and approve shop trespass requests.",
  },
  "/reports/workstation-version": {
    icon: "bi-diagram-3",
    title: "Workstation Versions",
    description: "Review workstation versions.",
  },
  "/reports/workstation-ip-analysis": {
    icon: "bi-wifi",
    title: "IP Analysis",
    description: "Analyze workstation IP usage.",
  },
  "/reports/workstation-ip-conflict-view": {
    icon: "bi-exclamation-diamond",
    title: "IP Conflict View",
    description: "Review workstation IP conflicts.",
  },
  "/reports/sync-bandwidth": {
    icon: "bi-activity",
    title: "Sync Bandwidth",
    description: "Review sync bandwidth usage.",
  },
  "/reports/storage-size": {
    icon: "bi-hdd",
    title: "Storage Size",
    description: "Review storage usage.",
  },
  "/reports/usage-summary-monthly": {
    icon: "bi-calendar-month",
    title: "Monthly Usage",
    description: "Review monthly usage summary.",
  },
  "/reports/usage-summary-weekly": {
    icon: "bi-calendar-week",
    title: "Weekly Usage",
    description: "Review weekly usage summary.",
  },
  "/reports/photobase-usage/devices": {
    icon: "bi-phone-vibrate",
    title: "PhotoBase Devices",
    description: "Review PhotoBase device usage.",
  },
  "/reports/data-usage": {
    icon: "bi-database",
    title: "Data Usage",
    description: "Review PhotoBase data usage.",
  },
  "/globalPromo": {
    icon: "bi-megaphone",
    title: "Global Promo",
    description: "Manage global promotions.",
  },
  "/parts-photo": {
    icon: "bi-image",
    title: "Parts Photo",
    description: "Open parts photo tools.",
  },
};

const getStorageKey = (user) => {
  const username = user && user.username ? user.username : "anonymous";
  return `${STORAGE_PREFIX}.${username}`;
};

const readPaths = (user) => {
  try {
    const value = window.localStorage.getItem(getStorageKey(user));
    const paths = value ? JSON.parse(value) : [];
    return Array.isArray(paths) ? paths.filter((path) => AREA_CATALOG[path]) : [];
  } catch (error) {
    return [];
  }
};

const writePaths = (user, paths) => {
  try {
    window.localStorage.setItem(getStorageKey(user), JSON.stringify(paths));
  } catch (error) {
    // Ignore storage failures so navigation never breaks.
  }
};

export const recordRecentArea = (user, pathname) => {
  if (!user || !pathname || !AREA_CATALOG[pathname]) return;

  const previousPaths = readPaths(user);
  const nextPaths = [pathname, ...previousPaths.filter((path) => path !== pathname)].slice(0, MAX_RECENT_AREAS);
  writePaths(user, nextPaths);
};

export const getRecentAreaPaths = (user) => readPaths(user);

export const getRecentAreas = (user, fallbackAreas) => {
  const recentAreas = readPaths(user).map((path) => ({
    to: path,
    ...AREA_CATALOG[path],
  }));
  const fallbackWithoutDuplicates = fallbackAreas.filter((area) => !recentAreas.some((recent) => recent.to === area.to));
  return [...recentAreas, ...fallbackWithoutDuplicates].slice(0, MAX_RECENT_AREAS);
};
